<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { navItems, seo } from '~/data/pages/banks/mocks'
import { WidgetBankPageAboutBankHeader } from '~/components/widget/bank-page'
import type { IBank } from '~/components/entity/bank'
import type { IResponseData } from '~/types'
import { WidgetSelectDepositType } from '~/components/widget/select-deposit-type'
import {
  WidgetOrganizationAsideContacts,
  mapBankToOrganizationInfo
} from '~/components/widget/organization-aside-contacts'
import { WidgetVacanciesList } from '~/components/widget/vacancies/list'
import { WidgetBankServicesMenu } from '~/components/widget/bank'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { useSeoFromCustom } from '~/composables/useSeoFromCustom'
import { WidgetSeoContent } from '~/components/widget/seo'

useSeoMeta(seo)
const route = useRoute()
const bankUrl = `/banks/${route.params.bank}`
const { data } = await useApiWith404<IResponseData<IBank>>(() => useUrlWithParams(bankUrl), {
  key: `banks-vacancies-${route.params.bank}`
})
const bank = computed(() => data.value?.data)
useEmptySectionNoindex(bank, 'vacancies_count')

const requestUrl = useRequestURL()
const baseUrl = requestUrl.origin

const seoData = computed(() => ({
  organization: {
    title: bank.value?.title
  }
}))

const { t, locale } = useI18n()

useSeoFromCustom(seoData, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.banks_of_kyrgyzstan'), url: '/banks' },
  { title: bank.value?.title || t('common.bank'), url: `/bank/${route.params.bank}` },
  { title: t('common.vacancies'), url: `/bank/${route.params.bank}/vacancies` }
])

const organizationInfo = computed(() => mapBankToOrganizationInfo(bank.value))
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts
              v-if="organizationInfo"
              :organization="organizationInfo"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <WidgetSelectDepositType :query-params="{ 'filter[position]': 'sidebar' }" />
          </aside>
        </template>
        <div class="space-y-5" role="main">
          <WidgetBankPageAboutBankHeader :bank="bank">
            <WidgetBankServicesMenu
              v-if="bank && bank.status === 'active'"
              :organization="bank"
              :nav-items="navItems"
            />
          </WidgetBankPageAboutBankHeader>
          <WidgetSeoContent
            heading-level="h2"
            heading-class="h3"
            leading-level="8"
            position="upper"
            :variables="{
              organization: bank?.official_title ?? ''
            }"
          />
          <WidgetVacanciesList
            v-if="bank && bank.id"
            :hide-organization-details="true"
            :query-params="{
              per_page: '20',
              'filter[organization_id]': bank.id.toString()
            }"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
